export interface ITaskFormJson {
    formHeader: IFormHeader;
    formBody?: IFormBodySection[];
    formAction: IFormAction[];
    /** Languages current schema is available in */
    availableLangs: string[];
    /**Form Data validation for a given field or entire form can be done through a custom endpoint */
    validationApi?: IApiDetails;
    /**Api to get taskRequestJson dynamically on load */
    taskRequestJsonApi?: IApiDetails;
    /**Form Data validation using JS code evaluation */
    validationFn?: string;
}

export interface IApiDetails {
    apiName: string;
    url: string;
    method: string;
    requestBody?: string;
    requestParams?: string;
    valueMapper?: any;
    optionsMapper?: any;
    sourceAppUrl?: string;
}

export interface IFormHeader {
    /** header key in TaskRequestJson */
    headerKey: string;
    /** subheader key in TaskRequestJson */
    subHeaderKey?: string;
    /**
     * dueDate key in TaskRequestJson
     * dueDate value has to be a string that's pipable by Angular's date pipe
     */
    dueDateKey?: string;
}

export interface IFormBodySection {
    /** Section header text */
    sectionHeader: {
        /** default display text */
        displayText: string;
        /** key to search text in translation file */
        i18nKey: string;
    };
    // flag to hide section on UI
    isDisplayedOnUI: boolean;
    // flag to hide the section on the downloaded pdf version
    isDisplayedOnPdf: boolean;
    // flag to show fields in table view
    isTableView?: boolean;
    editableGridProps?: {
        actions?: string[];
        tableColumns?: ITableColumn[];
        actionSchema?: ITaskFormJson;
    };
    // if isTableView is true, use this key to find collection of data in taskRequestJson
    tableDataKey?: string;
    /** external api info  */
    valueApi?: IApiDetails;
    /** Section description text */
    sectionDescription?: {
        /** default display text */
        displayText: string;
        /** key to search text in translation file */
        i18nKey: string;
    };
    fields: IField[];
    /**Form Section Data validation using JS code evaluation */
    validationFn?: string;
    /**
     * rules to display the field
     * field without displayedBy will always be displayed
     */
    displayedBy?: string;
}

export interface ITableColumn {
    displayName: string; // Column Name
    key: string; // Key to fetch the column data
}
export interface IFormAction {
    /** form action id */
    id: number;
    /** action display name */
    actionDisplayName: {
        displayText: string;
        i18nKey: string;
    };
    /** action section description for non-primary actions */
    sectionDescription?: {
        displayText: string;
        i18nKey: string;
    };
    /** action type */
    type: 'button' | 'file-upload' | 'checkbox' | 'submit' | 'link';
    /**
     * primary actions will be displayed in form footer
     * non-primary actions will be displayed in the form as an individual section
     */
    isPrimary: boolean;
    /** if user comment is required to perform the action.
     * This will open up a modal window with the comment text field
     */
    isCommentRequired?: boolean;
    // status value to be updated on click of action
    toTaskStatus?: string;
    /**
     * value binded to the action
     * not all actions have value - for example, button
     */
    value?: any;
    /**
     * rules to enable the action
     * action without enabledBy will always be enabled
     */
    enabledBy?: IActionEnableRule[];
    /**
     * additional props not covered above
     */
    props?: IFormActionProps;
}

export interface IField {
    /** field type will determine which control is rendered */
    type:
        | 'label'
        | 'input'
        | 'textarea'
        | 'attachments'
        | 'radio-button'
        | 'autocomplete'
        | 'multiAutocomplete'
        | 'richTextEditor';
    /** tsl-form-label */
    displayName?: {
        displayText: string;
        i18nKey: string;
    };
    /**
     * key for data binding
     * this key will be used to look for value from TaskRequestJson and construct TaskResponseJason
     */
    key: string;
    /** default value */
    value?: string;
    /** external api info  */
    optionsApi?: IApiDetails;
    /** tsl-tooltip */
    tooltip?: string;
    /** if the field is readonly */
    isReadonly?: boolean;
    /** if the field is disabled */
    isDisabled?: boolean;
    /** if the field is displayed */
    isDisplayed?: boolean;
    /**
     * column size of the field - default to 6
     * task form uses a 12 column layout, so by default each row displays 2 fields
     */
    colSize?: number;

    /**
     * rules to display the field
     * field without displayedBy will always be displayed
     */
    displayedBy?: string;

    /**
     * additional props not covered above
     */
    props?: IFieldProps;
}

export interface IFieldProps {
    /** if the field is required */
    isRequired?: boolean;
    /** a collection of IFieldOptions for a field having multiple selections */
    options?: IFieldOptions[];
    /** a key in taskRequest/taskResponse json to get options */
    optionsKey?: string;
    /**Form field Data validation using JS code evaluation */
    validationFn?: string;
    /** flag to disable select all options for multi select controls */
    isSelectAllDisabled?: boolean;

    isAutoCompleteApiInitiatedOnChange?: boolean;
    initialCollectionLoading?: boolean;
}

export interface IFormActionProps {
    /** comma-separated accepted file extensions for file-upload. e.g. '.pdf, .docx, .jpeg'  */
    accept?: string;
    /** whether file-upload allows multiple files  */
    multiple?: boolean;
    /** api to dynamically retrieve action data */
    valueApi?: IApiDetails;
    /** custom text displayed during link loading */
    linkLoaderText?: string;
}

export interface IFieldOptions {
    /** value to be bound to the control */
    value: any;
    /** display name for the option */
    displayName: string;
}

export interface IFieldRule {
    /** key of dependency field */
    key: string;
    /** validation function - need to be able to evaluate to a JS/TS function and return a boolean */
    validation: string;
    /**
     * example:
     * {
     *  key: 'email',
     *  validation: "(val) => val.length > 0"
     * }
     * means the current field will be displayed if field of id 1 has a val.length > 0
     */
}

export interface IActionEnableRule {
    /** id of dependency action */
    actionId: number;
    /** validation function - need to be able to evaluate to a JS/TS function and return a boolean */
    validation: string;
    /**
     * example:
     * {
     *  actionId: 1,
     *  validation: "(val) => val.length > 0"
     * }
     * means the current action will be enabled if action of id 1 has a val.length > 0
     */
}
export class IPhoneField {
    'number': string;
    'country': string;
    'countryCode': string;
    'invalid': boolean;
}
